// nodeFieldDisplayValue.js — which schema fields have something worth showing (visitor / read-only)

import { normalizeDateValue } from 'utilities/DateTime';
import { normalizeMeasurementValueForInput } from 'utilities/Measurement';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { normalizeNodelistKeys } from 'utilities/NodelistField';
import { normalizeSchemaFieldType } from 'utilities/FieldTypes';
import {
  getNodeSubtitle,
  isSubtitleFieldType,
} from 'utilities/NodeSubtitle';
import { getUrlString } from 'utilities/UrlFieldValue';
import { formatSignatureFieldDisplay, isSignatureFieldSigned } from 'utilities/SignatureFieldValue';

const hasNonEmptyString = (value) => String(value ?? '').trim().length > 0;

const hasTemporalDisplayValue = (value) => {
  if (value == null || value === '') return false;
  if (normalizeDateValue(value)) return true;
  if (typeof value !== 'object' || Array.isArray(value)) return false;
  return Boolean(
    normalizeDateValue(value.startDate)
    || normalizeDateValue(value.endDate)
    || normalizeDateValue(value.date)
    || normalizeDateValue(value.start)
    || normalizeDateValue(value.end),
  );
};

const hasAnnualDateDisplayValue = (value) => {
  if (value == null || value === '') return false;
  if (typeof value !== 'object' || Array.isArray(value)) {
    return hasNonEmptyString(value);
  }
  const month = Number(value.month);
  const day = Number(value.day);
  return (Number.isFinite(month) && month > 0) || (Number.isFinite(day) && day > 0);
};

const hasRichTextDisplayValue = (value) => {
  if (value == null || value === '') return false;
  if (typeof value === 'string') {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .length > 0;
  }
  if (typeof value !== 'object') return false;
  if (hasNonEmptyString(value.html)) return hasRichTextDisplayValue(value.html);
  if (hasNonEmptyString(value.text)) return true;
  try {
    return /"text"\s*:/.test(JSON.stringify(value));
  } catch {
    return false;
  }
};

const hasListDisplayValue = (value) => {
  const list = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
  return list.some((entry) => entry !== undefined && entry !== null && entry !== '');
};

const hasChecklistDisplayValue = (value) => {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value !== 'object') return false;
  const totalItems = Number(value.totalItems);
  if (Number.isFinite(totalItems) && totalItems > 0) return true;
  if (Array.isArray(value.itemTexts) && value.itemTexts.some((text) => hasNonEmptyString(text))) {
    return true;
  }
  return false;
};

const hasLocationDisplayValue = (value) => {
  if (value == null || value === '') return false;
  if (typeof value !== 'object' || Array.isArray(value)) return hasNonEmptyString(value);
  if (hasNonEmptyString(value.titleFull || value.title || value.formattedAddress || value.address)) {
    return true;
  }
  return value.lat != null && value.lng != null;
};

const hasMeasurementDisplayValue = (value) => {
  const normalized = normalizeMeasurementValueForInput(value);
  return hasNonEmptyString(normalized.value);
};

/**
 * Whether a field should render in visitor / preview Mode (valued fields only).
 *
 * @param {{ field: object, node: object, nodeTypeKey?: string }} params
 * @returns {boolean}
 */
export function hasNodeFieldDisplayValue({ field, node, nodeTypeKey } = {}) {
  const type = normalizeSchemaFieldType(field?.type);
  if (!type || type === 'qrcode') return false;

  // Not single "values" — hide on the valued-fields-only visitor surface.
  if (type === 'comments' || type === 'activity') return false;

  if (type === 'cover') {
    return hasNonEmptyString(node?.coverUrl);
  }
  if (type === 'color') {
    return hasNonEmptyString(node?.color);
  }
  if (isSubtitleFieldType(type)) {
    return hasNonEmptyString(getNodeSubtitle(node));
  }
  if (type === 'tags') {
    return Array.isArray(node?.tags) && node.tags.some((tag) => hasNonEmptyString(tag));
  }

  const raw = readNodeFieldRawValue(node, field, nodeTypeKey);
  if (raw === undefined || raw === null) return false;

  switch (type) {
    case 'boolean':
      // Explicit false is a real value; unset/null already returned above.
      return raw !== '';
    case 'number':
    case 'percentage':
    case 'money': {
      if (raw === '') return false;
      if (typeof raw === 'number') return Number.isFinite(raw);
      return hasNonEmptyString(raw) && Number.isFinite(Number(raw));
    }
    case 'currency':
      return hasNonEmptyString(raw);
    case 'multiselect':
    case 'people':
      return hasListDisplayValue(raw);
    case 'relation':
      return normalizeNodelistKeys(raw).length > 0;
    case 'checklist':
      return hasChecklistDisplayValue(raw);
    case 'date':
    case 'datetime':
    case 'daterange':
    case 'datetimerange':
      return hasTemporalDisplayValue(raw);
    case 'annualdate':
      return hasAnnualDateDisplayValue(raw);
    case 'location':
      return hasLocationDisplayValue(raw);
    case 'url':
      return hasNonEmptyString(getUrlString(raw));
    case 'signature':
      return isSignatureFieldSigned(raw);
    case 'measurement':
      return hasMeasurementDisplayValue(raw);
    case 'richtext':
      return hasRichTextDisplayValue(raw);
    case 'enum':
    case 'string':
    case 'email':
    case 'phone':
    case 'barcode':
    default:
      if (typeof raw === 'boolean') return true;
      if (typeof raw === 'number') return Number.isFinite(raw);
      if (Array.isArray(raw)) return hasListDisplayValue(raw);
      return hasNonEmptyString(raw);
  }
}

export default hasNodeFieldDisplayValue;
